import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, FilePdf } from '@phosphor-icons/react';
import VerificationOverlay from '@/components/verify/VerificationOverlay';
import { Button } from '@/components/ui/button';
import OvertimePrintSheet from '@/components/print/OvertimePrintSheet';
import LeavePrintSheet from '@/components/print/LeavePrintSheet';
import LeaveTransferPrintSheet from '@/components/print/LeaveTransferPrintSheet';
import WorkDeclarationPrintSheet from '@/components/print/WorkDeclarationPrintSheet';
import TravelExpensePrintSheet from '@/components/print/TravelExpensePrintSheet';
import TravelReceiptsPanel from '@/components/print/TravelReceiptsPanel';
import { buildPrintPlan, cycleBucketOf } from '@/lib/leaveTransferWeeks';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAGE_CLASS = {
  overtime: 'print-overtime preview-overtime',
  leave: 'print-leave preview-leave',
  leave_transfer: 'print-leave-transfer preview-leave-transfer',
  work_declaration: 'print-work-declaration preview-work-declaration',
  travel_expense: 'print-travel preview-travel',
};

// Chromium emits a trailing blank page when the last printed element uses a
// CSS named page but the root boxes stay unnamed — naming the roots after the
// LAST sheet closes the document on the same page. (Safari ignores `page:`.)
const PAGE_NAME = {
  overtime: 'overtime-page',
  leave: 'leave-page',
  leave_transfer: 'leave-transfer-page',
  work_declaration: 'work-declaration-page',
  travel_expense: 'travel-page',
};

const FORM_TYPE_LABEL = {
  overtime: '勤務指示書',
  leave: '休暇届',
  leave_transfer: '振替休暇申請書',
  work_declaration: '勤務時間申告書',
  travel_expense: '出張旅費精算書',
};

// Build a clean default file name for the browser "Save as PDF" dialog.
function buildPdfFilename(submissions) {
  if (!submissions.length) return '申請書';
  if (submissions.length === 1) {
    const s = submissions[0];
    const label = FORM_TYPE_LABEL[s.form_type] || '申請書';
    const name = (s.employee_name || '').replace(/[、,\s/\\]+/g, '_');
    return [label, name].filter(Boolean).join('_');
  }
  return `申請書_${submissions.length}件`;
}

function renderSheet(submission) {
  switch (submission.form_type) {
    case 'overtime': return <OvertimePrintSheet submission={submission} />;
    case 'leave': return <LeavePrintSheet submission={submission} />;
    case 'leave_transfer': return <LeaveTransferPrintSheet submission={submission} />;
    case 'work_declaration': return <WorkDeclarationPrintSheet submission={submission} />;
    case 'travel_expense': return <TravelExpensePrintSheet submission={submission} />;
    default: return <div>未対応のフォーム形式</div>;
  }
}

// 振替休暇申請書 だけ、選ばれた締め月のレコードを自動で足して1束にまとめる。
// 他の4種類には一切触らない（1レコード=1枚のまま）。
async function expandLeaveTransferWeeks(selected) {
  const picked = selected.filter((s) => s.form_type === 'leave_transfer');
  if (picked.length === 0) return { records: selected, autoIds: [] };
  const buckets = new Set(picked.map((s) => (cycleBucketOf(s) || {}).key).filter(Boolean));
  if (buckets.size === 0) return { records: selected, autoIds: [] };
  const all = await axios.get(`${API}/forms/submissions`, { params: { form_type: 'leave_transfer' } })
    .then((r) => r.data || [])
    .catch(() => []);
  const haveIds = new Set(selected.map((s) => s.id));
  const autoIds = [];
  const extra = all.filter((s) => {
    if (haveIds.has(s.id)) return false;
    const b = cycleBucketOf(s);
    return !!(b && buckets.has(b.key));
  });
  extra.forEach((s) => autoIds.push(s.id));
  return { records: [...selected, ...extra], autoIds };
}

export default function PrintPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [autoIds, setAutoIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const autoFiredRef = useRef(false);
  const isPdf = searchParams.get('mode') === 'pdf';
  const isAuto = searchParams.get('auto') === '1' || isPdf;

  useEffect(() => {
    const ids = searchParams.get('ids');
    if (!ids) return;
    (async () => {
      try {
        setLoading(true);
        const promises = ids.split(',').map((id) => axios.get(`${API}/forms/submissions/${id}`));
        const results = await Promise.all(promises);
        const picked = results.map((r) => r.data);
        // 振替休暇申請書 が含まれていれば、その週のレコードを足す（他の種類は素通し）
        const { records, autoIds: added } = await expandLeaveTransferWeeks(picked);
        setSubmissions(records);
        setAutoIds(added);
      } catch (error) {
        console.error('Failed to fetch submissions:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [searchParams]);

  // NOTE: window.print() must run synchronously inside the user gesture —
  // modern Chrome blocks it from timers/promise continuations. mark-printed
  // is therefore fired in the background, never awaited.
  const triggerPrint = () => {
    const ids = submissions.map((s) => s.id);
    if (ids.length) {
      axios.post(`${API}/forms/submissions/mark-printed`, { ids })
        .catch((e) => console.warn('mark-printed failed', e));
    }
    window.print();
  };

  // Auto-open the print / "Save as PDF" dialog once content (incl. images) is ready.
  useEffect(() => {
    if (loading || !isAuto || submissions.length === 0 || autoFiredRef.current) return;
    autoFiredRef.current = true;
    const prevTitle = document.title;
    document.title = buildPdfFilename(submissions);
    const t = setTimeout(() => {
      triggerPrint();
    }, 700);
    const restore = () => { document.title = prevTitle; };
    window.addEventListener('afterprint', restore, { once: true });
    return () => { clearTimeout(t); document.title = prevTitle; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, isAuto, submissions]);

  const handlePrint = triggerPrint;

  // 用紙の並び（振替休暇だけ週まとめ・7行ごとに分割される）
  const plan = buildPrintPlan(submissions);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-zinc-500">読み込み中...</p>
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-zinc-500">印刷する申請が見つかりません</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between no-print">
        <Button variant="outline" onClick={() => navigate('/history')} data-testid="back-button" className="border-zinc-300">
          <ArrowLeft size={18} className="mr-2" />
          履歴へ戻る
        </Button>
        <div className="flex items-center space-x-3">
          <span className="text-sm text-zinc-600" data-testid="print-count-label">
            {isPdf
              ? `${submissions.length}件（${plan.length}枚）をPDFで保存`
              : `${submissions.length}件（${plan.length}枚）を印刷`}
          </span>
          {isPdf ? (
            <Button onClick={handlePrint} data-testid="pdf-btn" className="bg-rose-600 hover:bg-rose-700">
              <FilePdf size={18} className="mr-2" />
              PDFで保存
            </Button>
          ) : (
            <Button onClick={handlePrint} data-testid="print-btn" className="bg-teal-700 hover:bg-teal-800">
              <Printer size={18} className="mr-2" />
              印刷する
            </Button>
          )}
        </div>
      </div>

      {isAuto && (
        <div className="no-print mb-3 rounded-md border border-sky-200 bg-sky-50 px-4 py-2.5 text-xs text-sky-800" data-testid="auto-print-hint">
          印刷ダイアログが自動で開かない場合は、右上の「{isPdf ? 'PDFで保存' : '印刷する'}」ボタンを押してください。
        </div>
      )}
      {isPdf && (
        <div className="no-print mb-6 rounded-md border border-rose-200 bg-rose-50 px-4 py-2.5 text-xs text-rose-800" data-testid="pdf-hint">
          印刷ダイアログが開いたら、送信先（プリンター）で「<span className="font-semibold">PDFに保存</span>」を選択してください。
        </div>
      )}

      {autoIds.length > 0 && (
        <div className="no-print mb-4 text-xs text-amber-900" data-testid="auto-expand-summary">
          同じ締め月・同じ申請部署の 振替休暇申請書 {autoIds.length}件 を自動で追加しました
          （合計 {plan.filter((e) => e.kind === 'leave_transfer').length}枚）。内訳は各用紙の上に表示しています。
        </div>
      )}

      {/* Root-named page (after the LAST sheet) kills Chromium's trailing blank page */}
      {plan.length > 0 && (
        <style>{`@media print { html, body, #root { page: ${
          PAGE_NAME[plan[plan.length - 1].kind === 'leave_transfer' ? 'leave_transfer' : plan[plan.length - 1].submission.form_type] || 'auto'
        }; } }`}</style>
      )}
      <div className="space-y-8 print-container">
        {plan.map((entry, idx) => {
          if (entry.kind === 'leave_transfer') {
            const single = entry.records.length === 1 ? entry.records[0] : null;
            const auto = entry.records.filter((s) => autoIds.includes(s.id));
            const picked = entry.records.filter((s) => !autoIds.includes(s.id));
            return (
              <div key={entry.key} className={idx > 0 ? 'page-break' : ''}>
                {auto.length > 0 && (
                  <div className="no-print mb-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-xs text-amber-900"
                    data-testid={`auto-expand-notice-${entry.key}`}>
                    <p className="font-semibold">
                      この用紙（{entry.bucket?.department} / 対象 {entry.bucket?.label}（{entry.bucket?.start} 〜 {entry.bucket?.end}）
                      {entry.pageCount > 1 ? ` / ${entry.pageIndex} / ${entry.pageCount}枚目` : ''}）
                      ：選択 {picked.length}件 ＋ 自動追加 {auto.length}件
                    </p>
                    <ul className="mt-1.5 space-y-0.5">
                      {auto.map((s) => (
                        <li key={s.id} data-testid={`auto-added-${s.id}`}>
                          ＋ {s.employee_name || '—'}（{(s.form_data || {}).holiday_date || '—'}）
                        </li>
                      ))}
                    </ul>
                    <p className="mt-1.5 text-[11px] text-amber-800">
                      自動追加された申請も「最終印刷日」が更新されます（印刷確認は各申請ごとの操作のままです）。
                    </p>
                  </div>
                )}
                <div className={PAGE_CLASS.leave_transfer} style={{ position: 'relative' }}>
                  <LeaveTransferPrintSheet
                    records={entry.records}
                    periodRange={entry.bucket}
                    pageIndex={entry.pageIndex}
                    pageCount={entry.pageCount}
                  />
                  {/* 複数件を1枚にまとめた用紙には承認欄を印字しない（紙にサインする） */}
                  {single && <VerificationOverlay verifications={single.verifications} />}
                </div>
              </div>
            );
          }
          const submission = entry.submission;
          return (
            <div key={submission.id} className={idx > 0 ? 'page-break' : ''}>
              <div
                className={`${PAGE_CLASS[submission.form_type] || ''}`}
                style={{ position: 'relative' }}
              >
                {renderSheet(submission)}
                <VerificationOverlay verifications={submission.verifications} />
              </div>
              {submission.form_type === 'travel_expense' && (
                <TravelReceiptsPanel submission={submission} />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
